import classNames from 'classnames';
import { FC, useEffect } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import {
  CreateEacEntitlementSubmitRequest,
  CreateEacEntitlementSubmitResponse,
} from '@amzn/coral_com-amazon-sssapinterface-model-operations-credits-submit';
import { ProductViewBase } from '@amzn/coral_com-amazon-sssapinterface-model-structures-credits';
import { KatProgress } from '@amzn/katal-react';
import { useBundle } from '@amzn/react-arb-tools';
import {
  ApiErrorBanner,
  API_ERROR_BANNER_VARIANT,
  DashboardItem,
  DASHBOARD_ITEM_VARIANT,
  FlatCard,
  ICON_NAME,
  SECTION_THEME,
  SignatoryBadge,
  SIGNATORY_BADGE_THEME,
  Tagline,
  TAGLINE_COLORS,
  TextInput,
  useApi,
  PrimaryButton,
  Loader,
  useDeviceSize,
} from '@amzn/sss-website-theme';

import { useSssApiCreditsClient } from 'src/clients';
import { isUkLaunchEnabled } from 'src/clients/weblab/weblab-client';
import { ErrorFallback } from 'src/components/common/error';
import { getEacCheckoutErrorStringId } from 'src/components/pages/checkout/checkout-steps/checkoutErrors';
import { CreditsComputedInfo } from 'src/components/pages/checkout/credits-computed-info/CreditsComputedInfo';
import { CHECKOUT_BUNDLE_NAME, COMMON_BUNDLE_NAME } from 'src/constants';
import { ROUTES } from 'src/enums';
import { getCurrencySymbol, shouldShowOriginalPrice, formatProductPrice, getImageRoot } from 'src/helpers';
import { calculateBundleQuantity } from 'src/helpers/bundles/bundle-helpers';
import { parseDateInput } from 'src/helpers/formatting/date-format-helpers';
import { removeNumberDecoration } from 'src/helpers/formatting/formatting-helpers';
import { t } from 'src/helpers/translations/translation-helpers';
import { CREDIT_SEGMENT_TAG_CONFIG, useProductTags, useUnifiedErrorHandler } from 'src/hooks';
import { useGetUserPreferences } from 'src/hooks/useGetUserPreferences.hook';

import { useCheckoutStep1Form, CheckoutStep1FormValues } from './CheckoutStep1.form';
import styles from './CheckoutStep1.module.scss';

const requestCreditsFormId = 'request-credits-form';

interface CheckoutStep1Props {
  product: ProductViewBase | null | undefined;
}

export const CheckoutStep1: FC<CheckoutStep1Props> = ({ product }) => {
  const { isSmallDesktop } = useDeviceSize();
  const imageRoot = getImageRoot(isSmallDesktop);
  const [commonBundle] = useBundle(COMMON_BUNDLE_NAME);
  const [checkoutBundle] = useBundle(CHECKOUT_BUNDLE_NAME);

  const creditsClient = useSssApiCreditsClient();
  const tags = useProductTags(product);
  const { userPreferredMarketplaceId, isLoading: isLoadingPreferences } = useGetUserPreferences();

  const showBundles = product?.productBundleFactor;
  const currencySymbol = getCurrencySymbol(product?.productOfferCurrency);

  const {
    quantityInputFormProps,
    dateInputFormProps,
    formIsCompleted,
    formHasErrors,
    handleSubmit,
    dateIsAfter90Days,
    dateFormat,
  } = useCheckoutStep1Form({
    showBundles: !!showBundles,
    productBundleFactor: product?.productBundleFactor,
    marketplaceId: userPreferredMarketplaceId,
  });

  const { asin } = useParams<{ asin: string; entitlementId: string }>();
  const navigate = useNavigate();

  // Unified error handling
  const { errorState, handleError, clearErrors } = useUnifiedErrorHandler({
    componentName: 'CheckoutStep1',
  });

  const {
    isLoading: isSubmitting,
    response,
    callApi,
  } = useApi<CreateEacEntitlementSubmitResponse>((err) => handleError(err, 'checkout-submission'));

  // Handle successful submission by navigating to next step with entitlementId
  useEffect(() => {
    if (response && asin) {
      if (response.entitlementId) {
        navigate(ROUTES.CHECKOUT_PAGE.replace(':asin', asin).replace(':entitlementId?', response.entitlementId));
      } else {
        // Missing entitlementId indicates an unexpected API response
        handleError(new Error('Missing entitlementId in API response'), 'checkout-submission');
      }
    }
  }, [response, asin, navigate, handleError]);

  const onSubmit: SubmitHandler<CheckoutStep1FormValues> = async (formValues) => {
    if (!asin || isSubmitting) return;

    // Clear any previous errors on submit
    clearErrors();

    const submitEntitlement = async () => {
      let quantity = parseInt(formValues.quantity.replace(/,/g, ''));
      const parsedDate = parseDateInput(formValues.date, dateFormat);
      const needByDate = parsedDate!.getTime();

      if (showBundles) {
        // Quantity of bundles requested (rounded up). Credit quantity calculated in SSSAPI
        quantity = calculateBundleQuantity(quantity, product.productBundleFactor);
      }

      const request = CreateEacEntitlementSubmitRequest.clone({
        asin: encodeURIComponent(asin),
        requestQuantity: quantity,
        requestNeedByDate: needByDate,
      });

      return creditsClient.createEacEntitlement(request);
    };

    callApi(submitEntitlement);
  };

  const isSubmitDisabled = formHasErrors || !formIsCompleted || isSubmitting;

  // Handle missing asin - routing error that shouldn't happen in normal flow
  if (!asin) {
    return <ErrorFallback />;
  }

  // Handle submission loading state
  if (isSubmitting) {
    return (
      <div className={styles.loaderWrapper}>
        <Loader label={t(commonBundle, 'loading_aria_label')} />
      </div>
    );
  }

  // Wait for user preferences to load before rendering the form (determines date format)
  if (isLoadingPreferences) {
    return (
      <div className={styles.loaderWrapper}>
        <Loader label={t(commonBundle, 'loading_aria_label')} />
      </div>
    );
  }

  // Get error banner configuration
  const getErrorBannerConfig = () => {
    // Extract errorType from API response if present
    const apiError = errorState.primaryError as any;

    // Coral errors nest errorType in receivedException, but test mocks set it directly
    const errorType = apiError?.receivedException?.errorType || apiError?.errorType;

    // Get appropriate error message
    const messageKey = errorType ? getEacCheckoutErrorStringId(errorType) : undefined;

    return {
      errors: errorState.errors,
      errorMessages: {
        generic: t(checkoutBundle, 'checkout_step_error_generic'),
        custom: messageKey ? t(checkoutBundle, messageKey) : undefined,
      },
      iconName: ICON_NAME.INFO_FILLED,
      testId: 'checkout-step-1-error-banner',
      variant: API_ERROR_BANNER_VARIANT.ERROR,
    };
  };

  const bannerConfig = errorState.hasErrors ? getErrorBannerConfig() : null;

  if (!product) {
    return (
      <div role="status">
        <KatProgress katAriaLabel={t(commonBundle, 'loading_aria_label')} indeterminate type="circular" />
      </div>
    );
  }

  const showOriginalPrice = shouldShowOriginalPrice(product);

  return (
    <section className={styles.checkoutStep1}>
      <FlatCard
        className={classNames(styles.card, styles.firstCard)}
        image={
          product.productDetailAtfHeroImage
            ? { src: `${imageRoot}/${product.productDetailAtfHeroImage}`, alt: product.productTitle }
            : undefined
        }
        variant={SECTION_THEME.Teal}
        title={t(checkoutBundle, 'checkout_step_1_first_card_title')}
        rows={[
          {
            label: t(checkoutBundle, 'checkout_first_card_name_title'),
            value: product.productTitle,
          },
          {
            label: t(checkoutBundle, 'checkout_first_card_segment_title'),
            value: t(commonBundle, CREDIT_SEGMENT_TAG_CONFIG[product.productCreditSegment].stringId),
          },
        ]}
      >
        <div className={styles.content}>
          <div className={styles.carbonCreditType}>
            <p className={styles.title}>{t(checkoutBundle, 'checkout_first_card_type_title')}</p>
            <ul className={styles.taglineList}>
              {tags.map((tag, i) => (
                <li key={`tag-${i}-${tag.copy}`}>
                  <Tagline className={styles.tagline} icon={tag.icon} color={TAGLINE_COLORS.TRANSPARENT}>
                    {tag.copy}
                  </Tagline>
                </li>
              ))}
            </ul>
          </div>

          <div className={styles.contractPrice}>
            <p className={styles.title}>{t(checkoutBundle, 'checkout_first_card_price_title')}</p>

            <div className={styles.priceRow}>
              {showOriginalPrice && (
                <p className={classNames(styles.body, styles.originalPrice)} data-testid="checkout-original-price">
                  {currencySymbol}
                  {formatProductPrice(product.productBaseOfferPrice)}
                </p>
              )}
              <p className={styles.body}>
                {t(
                  checkoutBundle,
                  isUkLaunchEnabled ? 'checkout_first_card_per_credit' : 'checkout_first_card_price_label',
                  {
                    currencySymbol,
                    price: formatProductPrice(product.productMatchedOfferPrice),
                  }
                )}
              </p>
              {showOriginalPrice && (
                <SignatoryBadge theme={SIGNATORY_BADGE_THEME.Light} data-testid="checkout-signatory-badge">
                  {t(checkoutBundle, 'checkout_step_1_signatory_price_tag')}
                </SignatoryBadge>
              )}
            </div>
          </div>
        </div>
      </FlatCard>
      <FlatCard
        className={classNames(styles.card, styles.secondCard)}
        title={t(checkoutBundle, 'checkout_step_1_second_card_title')}
        rows={[
          {
            label: t(checkoutBundle, 'checkout_step_1_second_card_subtitle'),
          },
        ]}
      >
        {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
        <form className={styles.form} id={requestCreditsFormId} onSubmit={handleSubmit(onSubmit)}>
          {bannerConfig && (
            <div className={styles.bannerWrapper}>
              <ApiErrorBanner {...bannerConfig} />
            </div>
          )}
          <div className={styles.formInputsGroup}>
            <TextInput
              data-testid="date-input"
              label={t(checkoutBundle, 'checkout_step_1_second_card_retirement_date_input_label')}
              {...dateInputFormProps}
              isRebrandingActive
            />

            <TextInput
              data-testid="quantity-input"
              label={t(checkoutBundle, 'checkout_step_1_second_card_quantity_input_label')}
              {...quantityInputFormProps}
              isRebrandingActive
            />

            {showBundles && (
              <p className={styles.batchNote}>
                {t(
                  checkoutBundle,
                  isUkLaunchEnabled
                    ? 'checkout_step_1_second_card_batch_note_long'
                    : 'checkout_step_1_second_card_batch_note',
                  {
                    batchSize: product.productBundleFactor,
                    currencySymbol,
                    price: showOriginalPrice
                      ? product.productBaseOfferBundlePrice
                      : product.productMatchedOfferBundlePrice,
                  }
                )}
              </p>
            )}

            {quantityInputFormProps.value && (
              <CreditsComputedInfo
                quantity={removeNumberDecoration(quantityInputFormProps.value)}
                showAllowedAdjustments={dateIsAfter90Days}
                product={product}
                marketplaceId={userPreferredMarketplaceId}
              />
            )}

            <div className={styles.dashboardItem}>
              <DashboardItem
                variant={DASHBOARD_ITEM_VARIANT.Disclaimer}
                icon={ICON_NAME.INFO_FILLED}
                label={t(checkoutBundle, 'checkout_step_1_second_card_disclaimer_title')}
                value={t(checkoutBundle, 'checkout_step_1_second_card_disclaimer_text')}
              />
            </div>
          </div>

          <PrimaryButton
            className={classNames(styles.submitCta, !isSubmitDisabled && styles.isSubmitEnabled)}
            label={t(checkoutBundle, 'checkout_step_1_second_card_submit_text')}
            icon={ICON_NAME.CHEVRON_RIGHT}
            type="submit"
            data-testid="request-credits-submit-button"
            form={requestCreditsFormId}
            disabled={isSubmitDisabled}
            isRebrandingActive
          />
        </form>
      </FlatCard>
    </section>
  );
};
